import moment from 'moment';
import React from 'react';

import OperatorDropdown from 'search/filters/base/OperatorDropdown';
import withDebouncing from 'search/filters/base/withDebouncing';
import {mapRelativeRangeValueToShortcutOperator} from 'search/filters/relativeRangeFilterUtils';
import SearchInput from 'search/inputs/SearchInput';
import {
  Filter,
  FILTERS,
  Operator,
  RelativeRangeUnits,
  Sort,
  STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY,
  StandardFilter,
  StandardFilterDataType,
  StandardRangeOperatorValue,
  StandardRelativeRangeOperatorValue,
} from 'search/SearchConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import DateField from 'ui-library/inputs/DateField';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import {LABEL_STYLES, Size} from 'ui-library/inputs/InputConstants';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import {CANONICAL_FORMAT, formalTime} from 'util/DateUtil';
import insertTextFromPaste from 'util/insertTextFromPaste';
import * as Keyboard from 'util/Keyboard';
import {listToReadable, typeToName} from 'util/NameUtil';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import {BaseFilterFormProps, FilterStyle} from './FilterFormConstants';
import makeRelativeDatetimeFilterFormNodes, {
  RelativeDatetimeFilterFormRange,
  RelativeRangeDirection,
} from './makeRelativeDatetimeFilterFormNodes';

export interface StandardFilterFormProps extends BaseFilterFormProps {
  filter: StandardFilter;
  filterStyle: FilterStyle;
  operators: Array<Operator>;
  onChangeFilter: (filter: StandardFilter) => void;
  onRemove?: () => void;
  onEnter?: () => void;
}

interface StandardFilterValueInputProps<TValue> {
  onChangeValue: (value: TValue) => void;
  value: TValue;
  placeholder?: string;
  onEnter?: () => void;
  isNullable?: boolean;
  searchInputPlaceholderOverride?: String;
}

interface EntityLinkProps {
  linkedTypes: Array<string>;
  sorts?: Array<Sort>;
  additionalFilters?: Array<Filter>;
}

function StandardFilterValueIntInput(props: StandardFilterValueInputProps<number | null>): JSX.Element {
  return (
    <NumericInput
      onChange={(e: NumericInputChangeEvent) => {
        props.onChangeValue(e.target.numericValue);
      }}
      placeholder={props.placeholder}
      value={props.value}
      validateInteger={true}
    />
  );
}

function StandardFilterValueFloatInput(props: StandardFilterValueInputProps<number | null>): JSX.Element {
  return (
    <NumericInput
      onChange={(e: NumericInputChangeEvent) => {
        props.onChangeValue(e.target.numericValue);
      }}
      placeholder={props.placeholder}
      value={props.value}
      validateInteger={false}
    />
  );
}

function StandardFilterValueDateInput(props: StandardFilterValueInputProps<string | null>): JSX.Element {
  return (
    <DateField
      labelStyle={LABEL_STYLES.HIDDEN}
      onChange={(value) => {
        props.onChangeValue(value !== '' ? value : null);
      }}
      placeholder={props.placeholder}
      value={props.value ?? ''}
    />
  );
}

function StandardFilterValueDatetimeInput(props: StandardFilterValueInputProps<string | null>): JSX.Element {
  return (
    <DatetimeField
      labelStyle={LABEL_STYLES.HIDDEN}
      onChange={(value) => {
        props.onChangeValue(value !== '' ? value : null);
      }}
      placeholder={props.placeholder}
      value={props.value ?? ''}
    />
  );
}

function InnerStandardFilterValueStringArrayInput(
  props: StandardFilterValueInputProps<Array<string>>
): JSX.Element {
  return (
    <TextInput
      onChange={(e: InputChangeEvent) => {
        props.onChangeValue(e.target.value.split(','));
      }}
      onKeyDown={Keyboard.callOnEnter(props.onEnter)}
      onPaste={insertTextFromPaste}
      placeholder="value"
      size={Size.SMALL}
      value={props.value}
      dataTestAttrs={{
        component: 'StandardFilterForm',
        element: 'textInput',
      }}
    />
  );
}

function InnerStandardFilterValueStringInput(props: StandardFilterValueInputProps<string>): JSX.Element {
  return (
    <TextInput
      onChange={(e: InputChangeEvent) => {
        props.onChangeValue(e.target.value);
      }}
      onKeyDown={Keyboard.callOnEnter(props.onEnter)}
      onPaste={insertTextFromPaste}
      placeholder="value"
      size={Size.SMALL}
      value={props.value}
      dataTestAttrs={{
        component: 'StandardFilterForm',
        element: 'textInput',
      }}
    />
  );
}

export const StandardFilterValueStringArrayInput = withDebouncing(InnerStandardFilterValueStringArrayInput);
export const StandardFilterValueStringInput = withDebouncing(InnerStandardFilterValueStringInput);

function computeEntityLinkInputPlaceholder(
  placeholder: string | undefined,
  value: string | null | Array<string | null>,
  linkedTypes: Array<string>
): string {
  const hasValue = Array.isArray(value) ? value.length > 0 : value != null;

  if (hasValue) {
    return '';
  }

  if (placeholder != null) {
    return placeholder;
  }

  return `Search for a ${listToReadable(
    linkedTypes.map((t) => typeToName(t)),
    'or'
  )}`;
}

function StandardFilterValueEntityLinkSingleInput(
  props: StandardFilterValueInputProps<string | null> & EntityLinkProps
): JSX.Element {
  return (
    <SearchInput
      autoFocus={false}
      disableTyping={props.value != null}
      isMulti={false}
      onSelect={(id) => {
        props.onChangeValue(id);
      }}
      onRemove={() => {
        props.onChangeValue(null);
      }}
      placeholder={computeEntityLinkInputPlaceholder(props.placeholder, props.value, props.linkedTypes)}
      selected={props.value == null ? [] : [props.value]}
      types={props.linkedTypes}
      sorts={props.sorts}
      additionalFilters={props.additionalFilters}
      includeNull={props.isNullable}
    />
  );
}

function StandardFilterValueEntityLinkListInput(
  props: StandardFilterValueInputProps<Array<string | null>> & EntityLinkProps
): JSX.Element {
  return (
    <SearchInput
      autoFocus={false}
      isMulti={true}
      onSelect={(id) => {
        props.onChangeValue([...props.value, id]);
      }}
      onRemove={(id) => {
        props.onChangeValue(props.value.filter((prevId) => prevId !== id));
      }}
      placeholder={computeEntityLinkInputPlaceholder(props.placeholder, props.value, props.linkedTypes)}
      selected={props.value}
      types={props.linkedTypes}
      sorts={props.sorts}
      additionalFilters={props.additionalFilters}
      includeNull={props.isNullable}
    />
  );
}

function makeIntFieldNodes(props: StandardFilterFormProps): Array<JSX.Element> {
  if ([Operator.EQ, Operator.GTE, Operator.LTE].includes(props.filter.operator)) {
    return [
      <StandardFilterValueIntInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as number | null}
        onEnter={props.onEnter}
      />,
    ];
  } else if ([Operator.BETWEEN].includes(props.filter.operator)) {
    const rangeValue = props.filter.value as StandardRangeOperatorValue;
    return [
      <StandardFilterValueIntInput
        key={`${props.filter.key}-from-input`}
        placeholder="from"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              from: newValue,
            },
          });
        }}
        value={rangeValue.from as number | null}
        onEnter={props.onEnter}
      />,
      <StandardFilterValueIntInput
        key={`${props.filter.key}-to-input`}
        placeholder="to"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              to: newValue,
            },
          });
        }}
        value={rangeValue.to as number | null}
        onEnter={props.onEnter}
      />,
    ];
  } else {
    throw new Error(`Unexpected operator for int field ${props.filter.operator}`);
  }
}

function makeFloatFieldNodes(props: StandardFilterFormProps): Array<JSX.Element> {
  if (
    [Operator.EQ, Operator.IS, Operator.IS_NOT, Operator.GTE, Operator.LTE].includes(props.filter.operator)
  ) {
    return [
      <StandardFilterValueFloatInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as number | null}
        onEnter={props.onEnter}
      />,
    ];
  } else if ([Operator.BETWEEN].includes(props.filter.operator)) {
    const rangeValue = props.filter.value as StandardRangeOperatorValue;
    return [
      <StandardFilterValueFloatInput
        key={`${props.filter.key}-from-input`}
        placeholder="from"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              from: newValue,
            },
          });
        }}
        value={rangeValue.from as number | null}
        onEnter={props.onEnter}
      />,
      <StandardFilterValueFloatInput
        key={`${props.filter.key}-to-input`}
        placeholder="to"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              to: newValue,
            },
          });
        }}
        value={rangeValue.to as number | null}
        onEnter={props.onEnter}
      />,
    ];
  } else {
    throw new Error(`Unexpected operator for float field ${props.filter.operator}`);
  }
}

// Relative range mapping to and from the UI elements is a bit complex because the relative range value is
// designed to be more flexible than the UI to support future extensibility without creating backwards
// compatibility issues. Specifically, the UI only supports a single range direction, whereas the filter
// API supports both a from and to range.
function filterValueToRelativeRangeProp(
  value: StandardRelativeRangeOperatorValue
): RelativeDatetimeFilterFormRange {
  if (value.fromUnits && value.toUnits) {
    throw new Error('StandardFilterForm only supports relative ranges with a range in a single direction.');
  }

  let direction: RelativeRangeDirection;
  let units: RelativeRangeUnits;
  let rangeValue: number | null;
  if (value.toUnits) {
    direction = RelativeRangeDirection.NEXT;
    units = value.toUnits;
    rangeValue = value.to;
  } else if (value.fromUnits) {
    direction = RelativeRangeDirection.LAST;
    units = value.fromUnits;
    rangeValue = value.from;
  } else {
    throw new Error('Must specify at least one of fromUnits or toUnits for relative range.');
  }

  return {
    direction,
    units,
    value: rangeValue,
  };
}

function relativeRangePropToFilterValue(
  range: RelativeDatetimeFilterFormRange
): StandardRelativeRangeOperatorValue {
  if (range.direction === RelativeRangeDirection.LAST) {
    return {
      from: range.value,
      fromUnits: range.units,
      to: null,
      toUnits: null,
    };
  } else if (range.direction === RelativeRangeDirection.NEXT) {
    return {
      from: null,
      fromUnits: null,
      to: range.value,
      toUnits: range.units,
    };
  } else {
    throw new Error(`Unexpected relative range direction ${range.direction}.`);
  }
}

function makeDateFieldNodes(props: StandardFilterFormProps): Array<JSX.Element> {
  if (
    [Operator.EQ, Operator.IS, Operator.IS_NOT, Operator.GTE, Operator.LTE].includes(props.filter.operator)
  ) {
    return [
      <StandardFilterValueDateInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as string | null}
        onEnter={props.onEnter}
      />,
    ];
  } else if ([Operator.BETWEEN].includes(props.filter.operator)) {
    const rangeValue = props.filter.value as StandardRangeOperatorValue;
    return [
      <StandardFilterValueDateInput
        key={`${props.filter.key}-from-input`}
        placeholder="from"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              from: newValue,
            },
          });
        }}
        value={rangeValue.from as string | null}
        onEnter={props.onEnter}
      />,
      <StandardFilterValueDateInput
        key={`${props.filter.key}-to-input`}
        placeholder="to"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              to: newValue,
            },
          });
        }}
        value={rangeValue.to as string | null}
        onEnter={props.onEnter}
      />,
    ];
  } else if (props.filter.operator === Operator.RELATIVE_RANGE) {
    const relativeRangeValue = props.filter.value as StandardRelativeRangeOperatorValue;

    return makeRelativeDatetimeFilterFormNodes({
      range: filterValueToRelativeRangeProp(relativeRangeValue),
      onChangeRange: (range: RelativeDatetimeFilterFormRange) => {
        props.onChangeFilter({
          ...props.filter,
          value: relativeRangePropToFilterValue(range),
        });
      },
      includeTimeUnits: false,
      buttonSize: props.buttonSize,
    });
  } else {
    throw new Error(`Unexpected operator for date field ${props.filter.operator}`);
  }
}

function makeDatetimeFieldNodes(props: StandardFilterFormProps): Array<JSX.Element> {
  if (
    [Operator.EQ, Operator.IS, Operator.IS_NOT, Operator.GTE, Operator.LTE].includes(props.filter.operator)
  ) {
    return [
      <StandardFilterValueDatetimeInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={
          moment(props.filter.value as string, [CANONICAL_FORMAT], true).isValid()
            ? formalTime(props.filter.value as string)
            : (props.filter.value as string | null)
        }
        onEnter={props.onEnter}
      />,
    ];
  } else if ([Operator.BETWEEN].includes(props.filter.operator)) {
    const rangeValue = props.filter.value as StandardRangeOperatorValue;
    return [
      <StandardFilterValueDatetimeInput
        key={`${props.filter.key}-from-input`}
        placeholder="from"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              from: newValue,
            },
          });
        }}
        value={
          moment(rangeValue.from as string, [CANONICAL_FORMAT], true).isValid()
            ? formalTime(rangeValue.from as string)
            : (rangeValue.from as string | null)
        }
        onEnter={props.onEnter}
      />,
      <StandardFilterValueDatetimeInput
        key={`${props.filter.key}-to-input`}
        placeholder="to"
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: {
              ...rangeValue,
              to: newValue,
            },
          });
        }}
        value={
          moment(rangeValue.to as string, [CANONICAL_FORMAT], true).isValid()
            ? formalTime(rangeValue.to as string)
            : (rangeValue.to as string | null)
        }
        onEnter={props.onEnter}
      />,
    ];
  } else if (props.filter.operator === Operator.RELATIVE_RANGE) {
    const relativeRangeValue = props.filter.value as StandardRelativeRangeOperatorValue;

    return makeRelativeDatetimeFilterFormNodes({
      range: filterValueToRelativeRangeProp(relativeRangeValue),
      onChangeRange: (range: RelativeDatetimeFilterFormRange) => {
        props.onChangeFilter({
          ...props.filter,
          value: relativeRangePropToFilterValue(range),
        });
      },
      includeTimeUnits: true,
      buttonSize: props.buttonSize,
    });
  } else {
    throw new Error(`Unexpected operator for datetime field ${props.filter.operator}`);
  }
}

function makeStringFieldNodes(props: StandardFilterFormProps): Array<JSX.Element> {
  if ([Operator.IS, Operator.IS_NOT].includes(props.filter.operator) || !Array.isArray(props.filter.value)) {
    return [
      <StandardFilterValueStringInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue === '' ? null : newValue,
          });
        }}
        initialValue={props.filter.value == null ? '' : (props.filter.value as string)}
        onEnter={props.onEnter}
      />,
    ];
  } else if (
    [
      Operator.INCLUDES_TEXT,
      Operator.EXCLUDES_TEXT,
      Operator.STARTSWITH_TEXT,
      Operator.IS_ONE_OF,
      Operator.IS_NONE_OF,
    ].includes(props.filter.operator)
  ) {
    const listValue = props.filter.value as Array<string>;
    return [
      <StandardFilterValueStringArrayInput
        key={`${props.filter.key}-array-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: isEmptyStringText(newValue as Array<string>) ? [] : newValue,
          });
        }}
        initialValue={listValue.join(',')}
        onEnter={props.onEnter}
      />,
    ];
  } else {
    throw new Error(`Unexpected operator for string field ${props.filter.operator}`);
  }
}

function makeSingleValueEntityLinkNodes(
  props: StandardFilterFormProps & Partial<EntityLinkProps>
): Array<JSX.Element> {
  const filterKey = props.filter.key;
  const entityLinkTypes = FILTERS[filterKey].entityLinkTypes;
  const placeholder = FILTERS[filterKey].searchInputPlaceholderOverride;
  if (entityLinkTypes == null) {
    throw new Error('Standard Entity Link Filter must specify entityLinkTypes');
  }
  if ([Operator.IS, Operator.IS_NOT].includes(props.filter.operator)) {
    return [
      <StandardFilterValueEntityLinkSingleInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as string | null}
        onEnter={props.onEnter}
        linkedTypes={entityLinkTypes}
        sorts={props.sorts}
        additionalFilters={props.additionalFilters}
        isNullable={FILTERS[filterKey].isNullable}
        placeholder={placeholder}
      />,
    ];
  } else if ([Operator.IS_ONE_OF, Operator.IS_NONE_OF].includes(props.filter.operator)) {
    return [
      <StandardFilterValueEntityLinkListInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as Array<string | null>}
        onEnter={props.onEnter}
        linkedTypes={entityLinkTypes}
        sorts={props.sorts}
        additionalFilters={props.additionalFilters}
        isNullable={FILTERS[filterKey].isNullable}
        placeholder={placeholder}
      />,
    ];
  } else {
    throw new Error(`Unexpected operator for single value entity link field ${props.filter.operator}`);
  }
}

function makeMultiValueEntityLinkNodes(props: StandardFilterFormProps & EntityLinkProps): Array<JSX.Element> {
  const filterKey = props.filter.key;
  const entityLinkTypes = props.linkedTypes ?? FILTERS[filterKey].entityLinkTypes;
  if (entityLinkTypes == null) {
    throw new Error('Standard Entity Link Filter must specify entityLinkTypes');
  }
  if ([Operator.HAS_ONE_OF, Operator.HAS_NONE_OF, Operator.HAS_ALL_OF].includes(props.filter.operator)) {
    return [
      <StandardFilterValueEntityLinkListInput
        key={`${props.filter.key}-input`}
        onChangeValue={(newValue) => {
          props.onChangeFilter({
            ...props.filter,
            value: newValue,
          });
        }}
        value={props.filter.value as Array<string | null>}
        onEnter={props.onEnter}
        linkedTypes={entityLinkTypes}
        sorts={props.sorts}
        additionalFilters={props.additionalFilters}
        // We shouldn't show the 'None' option for HAS_ALL_OF because a field cannot be null AND have other
        // values, so it doesn't make sense to allow that option to be selected. If the user wants to filter
        // to things that are only null, they should use IS_EMPTY instead.
        isNullable={FILTERS[filterKey].isNullable && props.filter.operator !== Operator.HAS_ALL_OF}
      />,
    ];
  } else {
    throw new Error(`Unexpected operator for multi value entity link field ${props.filter.operator}`);
  }
}

export function getValueNodesFactory(
  filterDataType: StandardFilterDataType
): (props: StandardFilterFormProps & Partial<EntityLinkProps>) => Array<JSX.Element> {
  let nodesFactory;
  switch (filterDataType) {
    case StandardFilterDataType.INT:
      nodesFactory = makeIntFieldNodes;
      break;
    case StandardFilterDataType.FLOAT:
      nodesFactory = makeFloatFieldNodes;
      break;
    case StandardFilterDataType.DATE:
      nodesFactory = makeDateFieldNodes;
      break;
    case StandardFilterDataType.DATETIME:
      nodesFactory = makeDatetimeFieldNodes;
      break;
    case StandardFilterDataType.STRING:
      nodesFactory = makeStringFieldNodes;
      break;
    case StandardFilterDataType.BOOL:
      throw new Error('Bool field unexpectedly had non-valueless operator.');
    case StandardFilterDataType.SINGLE_VALUE_ENTITY_LINK:
      nodesFactory = makeSingleValueEntityLinkNodes;
      break;
    case StandardFilterDataType.MULTI_VALUE_ENTITY_LINK:
      nodesFactory = makeMultiValueEntityLinkNodes;
      break;
    default:
      throw new Error(`Unexpected standard filter data type ${filterDataType}.`);
  }
  return nodesFactory;
}

export function getDisplayOperatorAndOperatorDropdown(
  filter: StandardFilter,
  onChangeFilter: (filter: StandardFilter) => void,
  buttonSize: ButtonSize,
  operators: Array<Operator>,
  isDirty?: boolean
): [Operator, JSX.Element] {
  const displayOperator =
    !isDirty && filter.operator === Operator.RELATIVE_RANGE
      ? mapRelativeRangeValueToShortcutOperator(filter.value as StandardRelativeRangeOperatorValue)
      : filter.operator;

  const operatorDropdown: JSX.Element = (
    <OperatorDropdown
      buttonSize={buttonSize}
      onChangeOperator={(operator) => {
        const defaultValueFactory = STANDARD_FILTER_OPERATOR_DEFAULT_VALUE_FACTORY[operator];
        if (!defaultValueFactory) {
          throw new Error(
            `Attempted to select operator ${operator} for standard filter, but no default value factory was found.`
          );
        }
        onChangeFilter({...filter, operator, value: defaultValueFactory()});
      }}
      operators={operators}
      selectedOperator={displayOperator}
    />
  );

  return [displayOperator, operatorDropdown];
}

// When we clear all the text in a filter input, the value will show up as [''].
// In that case, we'll want it to be treated as an empty array.
function isEmptyStringText(value: Array<string>): boolean {
  return value.length === 1 && value[0] === '';
}
